import { gql } from '@apollo/client'

import * as f from '../../../components/BPMTable/BPMInputs'
import {
    DataMapper,
    DataTransformer,
    GeneralChipComponentCreator,
    GeneralExcelDisplayCreator,
    StatusComponentCreator,
    BooleanExcelDisplayCreator,
    TableFieldDescription
} from '../../../components/BPMTable/TableMapperCreator'
import tableCreatorCreatorFactoryCreator from '../../../components/BPMTable/TableCreatorFactory/TableCreatorCreatorFactoryCreator'
import TableCreatorCreator from '../../../components/BPMTable/TableCreatorFactory/TableCreatorCreator'
import {DirectTableQueryBuilder} from "../../../components/BPMTable/TableQueryBuilder/DirectTableQueryBuilder";
import {RawUnitQueryBuilder} from "../../../components/BPMTable/TableQueryBuilder/RawUnitQueryBuilder";


const editMutator = gql`
    mutation editUserMutation(
        $customAttr1: String,
        $customAttr2: String,
        $customAttr3: String,
        $email: String,
        $role: String,
        $firstName: String,
        $middleName: String,
        $lastName: String,
        $isActive: Boolean,
        $costCenter: String,
        $manager: String,
        $title: String,
        $enterpriseId:String!,
        $language: String,
        $currency: String,
        $dateHired: String,
        $managedDomains: [Int],
        $domain: String!,
        $id: String!,
    ) {
        editUser(
            customAttr1: $customAttr1,
            customAttr2: $customAttr2,
            customAttr3: $customAttr3,
            email: $email,
            role: $role,
            firstName: $firstName,
            middleName: $middleName,
            costCenter: $costCenter,
            lastName: $lastName,
            enterpriseId: $enterpriseId,
            isActive: $isActive,
            manager: $manager,
            title: $title,
            language: $language,
            currency: $currency,
            dateHired: $dateHired,
            managedDomains: $managedDomains,
            domain: $domain,
            id: $id
        ) {
            user {
                id
            }
        }
    }
`

const newMutator = gql`
    mutation createUserMutation(
        $customAttr1: String,
        $customAttr2: String,
        $customAttr3: String,
        $dateHired: String,
        $email: String!,
        $role: String,
        $firstName: String,
        $middleName: String,
        $lastName: String,
        $costCenter: String,
        $isActive: Boolean,
        $enterpriseId:String!,
        $manager: String,
        $title: String,
        $language: String,
        $currency: String,
        $managedDomains: [Int],
        $domain: String!,
    ) {
        createUser(
            customAttr1: $customAttr1,
            customAttr2: $customAttr2,
            customAttr3: $customAttr3,
            dateHired : $dateHired,
            email: $email,
            firstName: $firstName,
            role: $role,
            middleName: $middleName,
            enterpriseId: $enterpriseId,
            costCenter: $costCenter,
            lastName: $lastName,
            isActive: $isActive,
            manager: $manager,
            title: $title,
            language: $language,
            currency: $currency,
            managedDomains: $managedDomains,
            domain: $domain
        ) {
            user {
                id
            }
        }
    }
`

const deleteMutator = gql`
    mutation removeUserMutation($ids: [String!], $allRecords: Boolean, $filters: String) {
        removeUser(ids: $ids, allRecords: $allRecords, filters: $filters) {
            user {
                id
            }
        }
    }
`

const unitQuery = `
    user(id: $id) {
        id
        email
        enterpriseId
        firstName
        middleName
        lastName
        isActive
        manager {
            id
            enterpriseId
            firstName
            lastName
            email
        }
        title
        customAttr1
        customAttr2
        customAttr3
        language {
            id
            codename
        }
        currency {
            code
        }
        dateHired
        dateTerminated
        role
        managedDomains {
            id
            name
        }
        domain {
            id
            name
        }
        costCenter {
            id
            displayName
            enterpriseId
            parent{
                enterpriseId
                displayName
            }
        }
    }
`


const InventoryUserTableCreatorQueries = { tableQueryBuilder: new DirectTableQueryBuilder('allUsers') , editMutator, newMutator, deleteMutator, unitQueryBuilder: new RawUnitQueryBuilder(unitQuery)}

class InventoryUserTableCreator extends TableCreatorCreator {
    constructor(filters) {
        super(filters)
    }

    static getId = () => {
        return "InventoryUserTableCreator"
    }

    getTableCreator = (tableName) => {
        const tableCreator = this.createTableCreator(this.constructor.getId(), tableName, InventoryUserTableCreatorQueries)
        tableCreator.setInitialSortField('isActive', 'desc')
        tableCreator.addDescriptions([
            new TableFieldDescription(
                'enterpriseId',
                new DataMapper("enterpriseId"),
                new f.FilterDescription(new f.TextFilter("enterpriseId")),
                {label: 'Enterprise Id'}
            ),
            new TableFieldDescription(
                'firstName',
                new DataMapper("firstName"),
                new f.FilterDescription(new f.TextFilter("firstName")),
                {label: 'First Name'}
            ),
            new TableFieldDescription(
                'middleName',
                new DataMapper("middleName"),
                new f.FilterDescription(new f.TextFilter("middleName")),
                {label: 'Middle Name', onlyForm: true}
            ),
            new TableFieldDescription(
                'lastName',
                new DataMapper("lastName"),
                new f.FilterDescription(new f.TextFilter("lastName")),
                {label: 'Last Name'}
            ),
            new TableFieldDescription(
                'role',
                new DataMapper("role"),
                new f.FilterDescription(new f.UsersRoleFilter("role")),
                {label: 'Role', transform: new DataTransformer(({role}) => role.charAt(0) + role.slice(1).toLowerCase())}
            ),
            new TableFieldDescription(
                'title',
                new DataMapper("title"),
                new f.FilterDescription(new f.TextFilter("title")),
                {label: 'Title'}
            ),
            new TableFieldDescription(
                'email',
                new DataMapper("email"),
                new f.FilterDescription(new f.TextFilter("email")),
                {label: 'Email'}
            ),
            new TableFieldDescription(
                'costCenter',
                new DataMapper("costCenter.displayName"),
                new f.FilterDescription(new f.NullTextFilter("costCenter")),
                {label: 'Cost Center'}
            ),
            new TableFieldDescription(
                'dateHired',
                new DataMapper("dateHired"),
                new f.FilterDescription(new f.PeriodFilter("dateHired")),
                {label: 'Date Hired'}
            ),
            new TableFieldDescription(
                'dateTerminated',
                new DataMapper("dateTerminated"),
                new f.FilterDescription(new f.PeriodFilter("dateTerminated")),
                {label: 'Date Terminated'}
            ),
            new TableFieldDescription(
                'managerEnterpriseId',
                new DataMapper("manager.enterpriseId"),
                new f.FilterDescription(new f.TextFilter("managerEnterpriseId")),
                {label: 'Manager ID'}
            ),
            new TableFieldDescription(
                'managerFirstName',
                new DataMapper("manager.firstName"),
                new f.FilterDescription(new f.TextFilter("managerFirstName")),
                {label: 'Manager Name'}
            ),
            new TableFieldDescription(
                'managerLastName',
                new DataMapper("manager.lastName"),
                new f.FilterDescription(new f.TextFilter("managerLastName")),
                {label: 'Manager Surname',onlyForm:true}
            ),
            new TableFieldDescription(
                'managedDomains',
                new DataMapper("managedDomains", "managedDomains.name"),
                new f.FilterDescription(new f.TextFilter("managedDomains")),
                {
                    label: 'Managed Domains',
                    onlyForm: true,
                    hideSort: true,
                    excelDisplay : new GeneralExcelDisplayCreator('Domain', [{label: 'Name', path: 'name'}]),
                    componentCreator: new GeneralChipComponentCreator(["name"], ["name"], null, false),
                    transform: new DataTransformer(({managedDomains})=>managedDomains)
                }
            ),
            new TableFieldDescription(
                'domain',
                new DataMapper("domain.name"),
                new f.FilterDescription(new f.TextFilter("domain")),
                {label: 'Domain', hideSort: true}
            ),
            new TableFieldDescription(
                'isActive',
                new DataMapper("isActive"),
                new f.FilterDescription(new f.BooleanFilter("isActive")),
                {label: 'Status',  componentCreator:new StatusComponentCreator(), excelDisplay: new BooleanExcelDisplayCreator()}
            ),
            new TableFieldDescription(
                'customAttr1',
                new DataMapper("customAttr1"),
                new f.FilterDescription(new f.TextFilter("customAttr1")),
                {label: 'Custom Attribute 1' , onlyForm: true}
            ),
            new TableFieldDescription(
                'customAttr2',
                new DataMapper("customAttr2"),
                new f.FilterDescription(new f.TextFilter("customAttr2")),
                {label: 'Custom Attribute 2' , onlyForm: true}
            ),
            new TableFieldDescription(
                'customAttr3',
                new DataMapper("customAttr3"),
                new f.FilterDescription(new f.TextFilter("customAttr3")),
                {label: 'Custom Attribute 3' , onlyForm: true}
            ),
            new TableFieldDescription(
                'language',
                new DataMapper("language.name"),
                new f.FilterDescription(new f.LanguageFilter("language")),
                {label: 'Language', onlyForm: true}
            ),
            new TableFieldDescription(
                'currency',
                new DataMapper("currency.code"),
                new f.FilterDescription(new f.TextFilter("currency")),
                {label: 'Currency', onlyForm: true}
            )
        ])

        return tableCreator;
    }
}

tableCreatorCreatorFactoryCreator.addTableCreatorCreator(InventoryUserTableCreator)

export {
    InventoryUserTableCreator,
    InventoryUserTableCreatorQueries
};

